<template>
  <Layout>
    <SeoMeta
      :title="`${useCase.name} AI Tools & Applications | Balan.ai`"
      :description="`Explore the best ${useCase.name.toLowerCase()} AI tools. Compare features and find the perfect ${useCase.name.toLowerCase()} automation software for your workflow. Browse our curated selection of AI applications.`"
      :keywords="`${useCase.name.toLowerCase()} ai tools, ${useCase.name.toLowerCase()} ai software, ${useCase.name.toLowerCase()} automation, artificial intelligence ${useCase.name.toLowerCase()}, ai ${useCase.name.toLowerCase()} apps`"
      :canonical="`/tools/category/${useCase.slug}`"
      pageType="tool"
      :breadcrumbs="[
        { label: 'Home', url: '/' },
        { label: 'AI Tools List', url: '/tools' },
        { label: useCase.name, url: `/tools/${useCase.slug}` }
      ]"
      :organization="{
        name: 'Balan.ai',
        url: 'https://www.balan.ai',
        logo: '/logo.png'
      }"
      :faqs="[
        {
          question: `What are the best AI tools for ${useCase.name.toLowerCase()}?`,
          answer: `Our curated list includes top-rated AI tools for ${useCase.name.toLowerCase()}, helping you find the right solution for your specific needs.`
        },
        {
          question: `How can AI improve ${useCase.name.toLowerCase()}?`,
          answer: `AI tools can automate and enhance ${useCase.name.toLowerCase()} tasks, saving time and improving quality. Explore our selection to find tools that match your workflow requirements.`
        }
      ]"
    />

    <main>
      <section aria-labelledby="category-heading" class="container mx-auto">
        <div class="mb-6 mt-16 lg:mt-0">
          <div class="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <!-- Back Icon Link -->
            <Link
              href="/tools"
              class="flex items-center bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white text-sm px-4 py-2 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              <Icon icon="mdi:arrow-left" class="text-xl mr-2" />
              All Tools
            </Link>

            <!-- Category Title -->
            <h1 id="category-heading" class="text-3xl lg:text-4xl font-bold text-center text-purple-400 relative">
              <span>{{ useCase.name }} - AI Tools</span>
              <span class="absolute inset-0 blur-lg opacity-50 text-purple-500">
                {{ useCase.name }} - AI Tools
              </span>
            </h1>

            <!-- Placeholder for layout balance -->
            <div class="hidden lg:block w-16"></div>
          </div>

          <!-- Category Description -->
          <p v-if="useCase.description" class="text-center text-gray-300 mt-4 max-w-3xl mx-auto">
            {{ useCase.description }}
          </p>
        </div>

        <!-- Filter Component -->
        <ToolFilter 
          :initial-filters="filters" 
          :useCase="useCase" 
          :platforms="platforms"
          :tags="tags"
          :features="features"
        />
        
        <!-- Active Filters Display -->
        <div class="container mx-auto mt-6">
          <ActiveFilterBadges :filters="filters" :useCase="useCase" />
        </div>

        <!-- Tools List -->
        <div class="mt-8">
          <ToolList :tools="tools.data" :startIndex="startIndex" />
        </div>

        <!-- Pagination -->
        <div class="mt-8">
          <Pagination :links="tools.links" />
        </div>
      </section>
    </main>
  </Layout>
</template>

<script setup>
import { computed } from 'vue';
import Layout from '@/Layouts/Layout.vue';
import SeoMeta from '@/Components/SeoMeta.vue';
import Pagination from '@/Components/Pagination.vue';
import ToolList from '@/Pages/Tools/ToolList.vue';
import ToolFilter from '@/Pages/Tools/ToolFilter.vue';
import ActiveFilterBadges from './ActiveFilterBadges.vue';
import { Link } from '@inertiajs/vue3';
import { Icon } from '@iconify/vue';

const props = defineProps({
  useCase: {
    type: Object,
    required: true,
  },
  tools: {
    type: Object,
    required: true,
  },
  startIndex: {
    type: Number,
    required: true,
  },
  filters: {
    type: Object,
    required: true,
  },
  platforms: {
    type: Array,
    required: true,
  },
  tags: {
    type: Array,
    required: true,
  },
  features: {
    type: Array,
    required: true,
  },
});

// Optional: Add computed properties for structured data
const toolsCount = computed(() => props.tools.total || 0);
</script>

<style scoped>
/* Existing styles remain the same */
</style>